import {
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  createProductCategory,
  updateProductCategory,
} from "../services/ProductCategoryService";
import { ProductCategory } from "../types/product-category.types";
import schema from "./schema";

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** When null, create. When set, edit that category. */
  editingCategory: ProductCategory | null;
};

export function ProductCategoryCreateModal({
  show,
  onClose,
  onSuccess,
  editingCategory,
}: Props) {
  const isEdit = editingCategory != null;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editingCategory?.name ?? "",
      isActive: editingCategory?.isActive ?? true,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        if (isEdit && editingCategory) {
          const res = await updateProductCategory(editingCategory.id, {
            name: values.name.trim(),
            isActive: values.isActive,
          });
          if (res.success) {
            toast.success(res.message);
            onSuccess();
            formik.resetForm();
            onClose();
          }
        } else {
          const res = await createProductCategory({
            name: values.name.trim(),
            isActive: values.isActive,
          });
          if (res.success) {
            toast.success(res.message);
            onSuccess();
            formik.resetForm();
            onClose();
          }
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ??
            err?.message ??
            (isEdit ? "Failed to update category" : "Failed to create category"),
        );
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal isOpen={show} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        {isEdit ? "Edit category" : "Add category"}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">
          Name must be unique. Categories assigned to products cannot be
          deleted until unused.
        </p>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <div className="mb-3">
            <Label className="form-label" htmlFor="pc-modal-name">
              Name
            </Label>
            <Input
              id="pc-modal-name"
              name="name"
              type="text"
              className="form-control"
              placeholder="e.g. Fasteners, Pumps"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.name && formik.errors.name)}
            />
            {formik.touched.name && formik.errors.name ? (
              <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
            ) : null}
          </div>
          <div className="form-check form-switch form-switch-md mb-4">
            <Input
              className="form-check-input"
              type="checkbox"
              id="pc-modal-active"
              name="isActive"
              checked={formik.values.isActive}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <Label className="form-check-label" htmlFor="pc-modal-active">
              Active (shown in selectors)
            </Label>
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <Button type="button" color="light" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting
                ? isEdit
                  ? "Saving…"
                  : "Creating…"
                : isEdit
                  ? "Update"
                  : "Create"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}
